function asNumber(value, label) {
  if (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
  ) {
    throw new Error(`${label}不能为空`);
  }

  const number = Number(value);
  if (!Number.isFinite(number)) {
    throw new Error(`${label}必须是数字`);
  }
  return number;
}

export function toProtocolHex(value, width, { scale = 1, label = "参数" } = {}) {
  const scaledValue = asNumber(value, label) * scale;
  const scaled = Math.round(scaledValue);
  const max = Math.pow(16, width) - 1;
  const precisionTolerance = Math.max(1e-8, Math.abs(scaledValue) * 1e-12);

  if (Math.abs(scaledValue - scaled) > precisionTolerance) {
    throw new Error(`${label}的精度不符合协议要求`);
  }

  if (scaled < 0 || scaled > max) {
    throw new Error(`${label}超出协议允许范围`);
  }

  return scaled.toString(16).padStart(width, "0").toUpperCase();
}

function buildByte(value, label) {
  return toProtocolHex(value, 2, { label });
}

function buildRangedByte(value, min, max, label) {
  const number = asNumber(value, label);
  if (!Number.isInteger(number) || number < min || number > max) {
    throw new Error(`${label}必须在${min}到${max}之间`);
  }

  return buildByte(number, label);
}

function buildHourByte(value, label) {
  return buildRangedByte(value, 0, 23, label);
}

function buildMinuteByte(value, label) {
  return buildRangedByte(value, 0, 59, label);
}

export function buildSystemCommand(form) {
  const fields = [
    buildByte(form.jinShuiYaLiDi, "进水压力过低值"),
    buildRangedByte(form.chunShuiXiangZhiShuiTingZhi, 0, 100, "制水停止水位"),
    buildRangedByte(form.chunShuiXiangZhiShuiQiDong, 0, 100, "制水启动水位"),
    buildByte(form.moChongXiShiChang, "膜冲洗时长"),
    buildByte(form.moQianYaLiGao, "膜前压力过高值"),
    buildByte(form.gongShuiYaLiDi, "供水压力过低值"),
    buildByte(form.gongShuiBangQiDong, "供水泵启动值"),
    buildByte(form.gongShuiBangTingZhi, "供水泵停止值"),
    buildByte(form.chunShuiXiangPaiKong, "纯水箱排空设置"),
    buildByte(form.jinShuiLiuLiangXiShu, "进水流量系数"),
    buildByte(form.chunShuiLiuLiangXiShu, "纯水流量系数"),
    buildByte(form.gongShuiLiuLiangXiShu, "供水流量系数"),
    buildByte(form.zhiShuiChongXiJianGe, "制水冲洗间隔"),
    buildByte(form.jinShuiYaLiGao, "进水压力过高值"),
    buildRangedByte(form.chunShuiXiangGongShuiQiDong, 0, 100, "供水启动水位"),
    buildRangedByte(form.chunShuiXiangGongShuiTingZhi, 0, 100, "供水停止水位"),
    buildByte(form.chunShuiXunHuanJianGe, "纯水循环间隔"),
    buildByte(form.chunShuiXunHuanShiChang, "纯水循环时长")
  ];

  return `AA02${fields.join("")}`;
}

export function buildTimingCommand(form) {
  const fields = [
    buildByte(form.setIntervalDays, "设置间隔天数"),
    buildByte(form.accumulatedDays, "已累计间隔天数")
  ];

  for (let period = 1; period <= 3; period += 1) {
    fields.push(
      buildHourByte(
        form[`waterReturnValveStartHour${period}`],
        `时间段${period}回水阀开始小时`
      ),
      buildMinuteByte(
        form[`waterReturnValveStartMinute${period}`],
        `时间段${period}回水阀开始分钟`
      ),
      buildHourByte(
        form[`waterReturnValveEndHour${period}`],
        `时间段${period}回水阀结束小时`
      ),
      buildMinuteByte(
        form[`waterReturnValveEndMinute${period}`],
        `时间段${period}回水阀结束分钟`
      ),
      buildHourByte(
        form[`deviceStartHour${period}`],
        `时间段${period}装置开始小时`
      ),
      buildMinuteByte(
        form[`deviceStartMinute${period}`],
        `时间段${period}装置开始分钟`
      ),
      buildHourByte(
        form[`deviceEndHour${period}`],
        `时间段${period}装置结束小时`
      ),
      buildMinuteByte(
        form[`deviceEndMinute${period}`],
        `时间段${period}装置结束分钟`
      )
    );
  }

  return `AA05${fields.join("")}`;
}

export function buildClockCommand(form) {
  return `AA06${[
    buildRangedByte(form.year, 0, 99, "年份"),
    buildRangedByte(form.month, 1, 12, "月份"),
    buildRangedByte(form.day, 1, 31, "日期"),
    buildHourByte(form.hour, "小时"),
    buildMinuteByte(form.minute, "分钟")
  ].join("")}`;
}

function buildFilterFlow(value, label) {
  return toProtocolHex(value, 4, { label });
}

function buildFilterDays(value, label) {
  return toProtocolHex(value, 4, { label });
}

export function buildFilterCommand(form) {
  const fields = [
    buildFilterFlow(form.quartzSandFlow, "石英砂流量"),
    buildFilterDays(form.quartzSandDays, "石英砂天数"),
    buildFilterFlow(form.activatedCarbonFlow, "活性炭流量"),
    buildFilterDays(form.activatedCarbonDays, "活性炭天数"),
    buildFilterFlow(form.softeningResinFlow, "软化树脂流量"),
    buildFilterDays(form.softeningResinDays, "软化树脂天数"),
    buildFilterFlow(form.regenerationSaltFlow, "再生盐流量"),
    buildFilterDays(form.regenerationSaltDays, "再生盐天数"),
    buildFilterFlow(form.precisionFilterFlow, "精密滤芯流量"),
    buildFilterDays(form.precisionFilterDays, "精密滤芯天数"),
    buildFilterFlow(form.roMembraneFlow, "RO/NF膜流量"),
    buildFilterDays(form.roMembraneDays, "RO/NF膜天数"),
    "0032",
    "016D"
  ];

  return `AA03${fields.join("")}`;
}

const COMMAND_LENGTHS = {
  AA02: 40,
  AA03: 60,
  AA04: 13,
  AA05: 56,
  AA06: 14
};

/**
 * Ensures the generated `ms` value satisfies the fixed-length requirement in
 * the device HTTP protocol before it can be sent to the device.
 */
export function validateProtocolCommand(command) {
  if (typeof command !== "string" || !/^[0-9A-F]+$/i.test(command)) {
    throw new Error("转换后的协议格式无效");
  }

  const prefix = command.slice(0, 4).toUpperCase();
  const expectedLength = COMMAND_LENGTHS[prefix];
  if (!expectedLength) {
    throw new Error("不支持的设备指令类型");
  }

  if (command.length !== expectedLength) {
    throw new Error(
      `${prefix}协议长度错误，应为${expectedLength}个十六进制字符`
    );
  }

  return command.toUpperCase();
}
